import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/notify";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LifeBuoy, MessageSquare, Clock, Send, ArrowLeft,
  Search, Inbox, AlertCircle, CheckCircle2, Loader2
} from "lucide-react";
import { getAccessToken } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  in_progress: { label: "In Progress", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  resolved: { label: "Resolved", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  closed: { label: "Closed", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

export default function SupportManagementPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [newTicket, setNewTicket] = useState({ category: "", subject: "", message: "" });

  const token = getAccessToken();

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/support/tickets`, { headers }),
        axios.get(`${API}/api/support/stats`, { headers }),
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load support data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const headers = { Authorization: `Bearer ${token}` };

  const viewTicket = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/api/support/tickets/${ticketId}`, { headers });
      setSelectedTicket(res.data);
    } catch {
      toast.error("Failed to load ticket");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/support/tickets/${selectedTicket.id}/reply`, { message: reply }, { headers });
      toast.success("Reply sent");
      setReply("");
      viewTicket(selectedTicket.id);
      fetchData();
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/api/support/tickets/${ticketId}/status?status=${status}`, {}, { headers });
      toast.success(`Ticket marked as ${status}`);
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((prev) => ({ ...prev, status }));
      }
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const createTicket = async () => {
    if (!newTicket.category || !newTicket.subject || !newTicket.message) {
      toast.error("All fields are required"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/support/tickets`, newTicket, { headers });
      toast.success("Ticket created");
      setShowCreate(false);
      setNewTicket({ category: "", subject: "", message: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create ticket");
    } finally { setSubmitting(false); }
  };

  const filtered = tickets.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (search) {
      // Trim leading/trailing whitespace before matching.
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        t.subject?.toLowerCase().includes(q) ||
        t.investor_name?.toLowerCase().includes(q) ||
        t.investor_email?.toLowerCase().includes(q) ||
        t.admin_name?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Ticket Detail View
  if (selectedTicket) {
    const st = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.open;
    return (
      <div className="space-y-6" data-testid="admin-ticket-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)} className="gap-2" data-testid="back-to-tickets-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Tickets
        </Button>

        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-xl" data-testid="admin-ticket-subject">{selectedTicket.subject}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="font-medium text-foreground">{selectedTicket.tagged_to === "fund_manager" ? selectedTicket.admin_name : selectedTicket.investor_name}</span>
                  {" "}&middot; {selectedTicket.tagged_to === "fund_manager" ? selectedTicket.admin_email : selectedTicket.investor_email} &middot; {selectedTicket.category} &middot; {formatDate(selectedTicket.created_at)}
                  {selectedTicket.tagged_to && (
                    <Badge variant="outline" className="ml-2 text-[10px]">{selectedTicket.tagged_to === "fund_manager" ? "Fund Manager" : "Investor"}</Badge>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${st.color} border`} data-testid="admin-ticket-status">{st.label}</Badge>
                {(selectedTicket.status !== "resolved" && selectedTicket.status !== "closed") ? (
                  <Select value={selectedTicket.status} onValueChange={(v) => updateStatus(selectedTicket.id, v)}>
                    <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="status-change-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs text-muted-foreground">Only investor can reopen</span>
                )}
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`p-4 rounded-lg ${msg.sender_role === "investor" ? "bg-primary/10 ml-8" : "bg-secondary mr-8"}`}
                    data-testid={`admin-message-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">
                        {msg.sender_name}
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          {msg.sender_role === "investor" ? "Investor" : msg.sender_role === "super_admin" ? "Super Admin" : "Fund Manager"}
                        </Badge>
                      </span>
                      <span className="text-xs text-muted-foreground">{formatDate(msg.timestamp)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {selectedTicket.status !== "closed" && (
              <div className="mt-4 flex gap-2">
                <Textarea
                  placeholder="Type your reply..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="admin-reply-input"
                />
                <Button onClick={sendReply} disabled={submitting || !reply.trim()} className="self-end" data-testid="admin-send-reply-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6" data-testid="support-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-primary" />
            Support Tickets
          </h1>
          <p className="text-muted-foreground mt-1">
            {user?.role === "super_admin" ? "Manage all support tickets across funds" : "Manage support tickets for your funds"}
          </p>
        </div>
        {user?.role === "admin" && (
          <Button onClick={() => setShowCreate(true)} className="gap-1.5" data-testid="create-ticket-btn">
            <MessageSquare className="w-4 h-4" /> Create Ticket
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="support-stats">
          <Card className="stat-card bg-violet-500/10 border-violet-500/20">
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total</span>
              <p className="font-heading text-2xl font-bold mt-1 text-violet-500 dark:text-violet-400" data-testid="stat-total">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="stat-card bg-blue-500/10 border-blue-500/20">
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Open</span>
              <p className="font-heading text-2xl font-bold mt-1 text-blue-500 dark:text-blue-400" data-testid="stat-open">{stats.open}</p>
            </CardContent>
          </Card>
          <Card className="stat-card bg-amber-500/10 border-amber-500/20">
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">In Progress</span>
              <p className="font-heading text-2xl font-bold mt-1 text-amber-500 dark:text-amber-400" data-testid="stat-in-progress">{stats.in_progress}</p>
            </CardContent>
          </Card>
          <Card className="stat-card bg-emerald-500/10 border-emerald-500/20">
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Resolved</span>
              <p className="font-heading text-2xl font-bold mt-1 text-emerald-500 dark:text-emerald-400" data-testid="stat-resolved">{stats.resolved}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="support-search-input"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList data-testid="status-filter-tabs">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="open">Open</TabsTrigger>
            <TabsTrigger value="in_progress">In Progress</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
            <TabsTrigger value="closed">Closed</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Ticket List */}
      {filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No tickets found</h3>
            <p className="text-muted-foreground">
              {tickets.length === 0 ? "No support tickets have been raised yet." : "No tickets match your current filters."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((ticket) => {
            const st = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            return (
              <Card
                key={ticket.id}
                className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => viewTicket(ticket.id)}
                data-testid={`admin-ticket-${ticket.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium truncate">{ticket.subject}</h3>
                        <Badge className={`${st.color} border text-[10px]`}>{st.label}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                        <span className="font-medium text-foreground/80">{ticket.tagged_to === "fund_manager" ? ticket.admin_name : ticket.investor_name}</span>
                        {ticket.tagged_to && (
                          <Badge variant="outline" className="text-[9px] h-4">{ticket.tagged_to === "fund_manager" ? "FM" : "Investor"}</Badge>
                        )}
                        <span>&middot;</span>
                        <span>{ticket.category}</span>
                        <span>&middot;</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(ticket.updated_at)}
                        </span>
                        <span>&middot;</span>
                        <span>{ticket.messages?.length || 1} message{(ticket.messages?.length || 1) !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <MessageSquare className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Ticket Dialog for Fund Managers */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="create-ticket-dialog">
          <Card className="w-full max-w-lg mx-4 bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Create Support Ticket</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
                <Select value={newTicket.category} onValueChange={v => setNewTicket(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="new-ticket-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {["General Inquiry", "Document Request", "Transaction Query", "Technical Issue"].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject</label>
                <Input value={newTicket.subject} onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))} placeholder="Brief description of your issue" data-testid="new-ticket-subject" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Message</label>
                <Textarea value={newTicket.message} onChange={e => setNewTicket(p => ({ ...p, message: e.target.value }))} placeholder="Describe your issue in detail..." className="min-h-[100px]" data-testid="new-ticket-message" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={createTicket} disabled={submitting} data-testid="submit-ticket-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Ticket
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}